import { useMemo } from 'react';

import { Box, Spinner } from '@chakra-ui/react';

import { useGetInventoryDefinitionsByLetterQuery } from '@/graphql/generated/graphql';
import { useLocale } from '@/hooks';
import { InventoryTitles } from '@/utils/models/inventory-items';

import { TitlesList } from './TitlesList';

interface LazyTitlesByLetterProps {
  letter: string;
  isSingleColumn?: boolean;
  basePath?: string;
  withLeftMargin?: boolean;
  visibleCount?: number;
  showToggle?: boolean;
  moreLabel?: string;
  lessLabel?: string;
}

export const LazyTitlesByLetter = ({
  letter,
  isSingleColumn,
  basePath,
  withLeftMargin,
  visibleCount,
  showToggle,
  moreLabel,
  lessLabel,
}: LazyTitlesByLetterProps) => {
  const locale = useLocale();
  const { data, isLoading } = useGetInventoryDefinitionsByLetterQuery({
    locale,
    startsWith: letter,
  });

  const titles: InventoryTitles[] = useMemo(() => {
    const items = data?.inventoryDefinitions?.data ?? [];
    return items
      .map((d) => ({
        title: d.attributes?.slug?.replaceAll('-', ' ') || '',
        slug: d.attributes?.slug || '',
      }))
      .filter((t) => t.title && t.slug)
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [data]);

  if (isLoading) {
    return (
      <Box mt={{ base: 8, sm: 0 }} ml={withLeftMargin ? { sm: 14 } : undefined}>
        <Spinner />
      </Box>
    );
  }

  return (
    <TitlesList
      titles={titles}
      isSingleColumn={isSingleColumn}
      basePath={basePath}
      withLeftMargin={withLeftMargin}
      visibleCount={visibleCount}
      showToggle={showToggle}
      moreLabel={moreLabel}
      lessLabel={lessLabel}
    />
  );
};
